import { useEffect, useState } from 'react';
import api from '../../services/api';
import type { WatchEvent, WatchRunPages } from '../../types';
import { asHref, when } from './watchEventLines';

/** How many pages of a section show before "Show all". */
const SHOWN = 15;

/** Alert kinds that are lists of pages: the page list says them better. */
const PAGE_KINDS = new Set(['new_section_launched', 'new_pages_published', 'pages_removed']);

function Section({ section, pages, isNew }: { section: string | null; pages: string[]; isNew: boolean }) {
  const [all, setAll] = useState(false);
  const shown = all ? pages : pages.slice(0, SHOWN);
  return (
    <div className="mt-1.5">
      <div className="flex items-baseline gap-2 text-xs">
        <span className="font-medium text-gray-800 dark:text-gray-100">{section ?? 'Other pages'}</span>
        <span className="text-gray-500 dark:text-gray-400">
          {pages.length} page{pages.length === 1 ? '' : 's'}
        </span>
        {isNew && (
          <span className="rounded bg-indigo-50 px-1.5 text-[11px] text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200">
            new section
          </span>
        )}
      </div>
      <ul className="mt-0.5 flex flex-col gap-0.5 pl-3 text-xs">
        {shown.map(p => {
          const href = asHref(p);
          return (
            <li key={p} className="truncate">
              {href ? (
                <a
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  className="text-indigo-600 hover:underline dark:text-indigo-300"
                >
                  {p}
                </a>
              ) : (
                p
              )}
            </li>
          );
        })}
      </ul>
      {pages.length > SHOWN && (
        <button
          type="button"
          onClick={() => setAll(a => !a)}
          className="mt-0.5 pl-3 text-xs text-indigo-600 hover:underline dark:text-indigo-300"
        >
          {all ? 'Show fewer' : `Show all ${pages.length}`}
        </button>
      )}
    </div>
  );
}

/**
 * WHAT ONE CHECK CHANGED, AS PAGES. Opening a check that raised eighteen
 * alerts showed eighteen boxes, each with ten example pages. This is the check
 * itself: every page added and removed, by section, each a link, with the
 * alerts that are not page lists (a publishing pace) said above it.
 */
export default function PageChanges({
  runId,
  events = [],
  only,
}: {
  runId: number;
  events?: WatchEvent[];
  /** Just one side, for an alert about added or removed pages. */
  only?: 'added' | 'removed';
}) {
  const [data, setData] = useState<WatchRunPages | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    api
      .getWatchRunPages(runId)
      .then(d => alive && setData(d))
      .catch(e => alive && setError(e instanceof Error ? e.message : 'Could not load the pages'));
    return () => {
      alive = false;
    };
  }, [runId]);
  const newSections = new Set(
    events.filter(e => e.kind === 'new_section_launched').map(e => String(e.payload?.where ?? '')),
  );
  const notes = events.filter(e => !PAGE_KINDS.has(e.kind));
  if (error) return <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>;
  if (!data) return <p className="mt-2 text-xs text-gray-400">Loading the pages…</p>;
  const sides = (['added', 'removed'] as const).filter(s => (only ? s === only : true) && data[`${s}_count`] > 0);
  return (
    <div className="mt-2 border-t border-gray-100 pt-2 dark:border-gray-700" data-testid={`page-changes-${runId}`}>
      {data.compared_at && (
        <p className="text-xs text-gray-500 dark:text-gray-400">Compared with the check of {when(data.compared_at)}.</p>
      )}
      {notes.map(e => (
        <p key={e.id} className="mt-1 text-xs text-gray-700 dark:text-gray-200">
          <span className="font-medium">{e.headline}.</span> {e.so_what}
        </p>
      ))}
      {sides.map(side => (
        <div key={side} className="mt-2">
          <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-200">
            {side === 'added' ? 'Added' : 'Removed'} {data[`${side}_count`]} page
            {data[`${side}_count`] === 1 ? '' : 's'}
          </h4>
          {data[side].map(g => (
            <Section
              key={g.section ?? 'other'}
              section={g.section}
              pages={g.pages}
              isNew={side === 'added' && !!g.section && newSections.has(g.section)}
            />
          ))}
        </div>
      ))}
      {!sides.length && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">No pages added or removed.</p>}
    </div>
  );
}
